import React, { useState, useEffect } from 'react';
import { Trophy, Zap, Clock, Save, Loader2, ToggleLeft, ToggleRight, Users, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

/**
 * AdminLeaderboardSettings Component
 * 
 * Admin settings panel for response time leaderboard:
 * - Quick responder threshold
 * - Minimum responses required
 * - Enable/disable features
 */
export default function AdminLeaderboardSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    quick_responder_threshold_minutes: 5,
    minimum_responses_required: 3,
    leaderboard_size: 5,
    badge_enabled: true,
    leaderboard_lookback_days: 7
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/response-time/settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/response-time/stats`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/response-time/settings`,
        settings,
        { withCredentials: true }
      );
      toast.success('Leaderboard settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="h-32 bg-slate-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="leaderboard-settings">
      <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
        <Trophy className="h-5 w-5 text-amber-500" />
        Response Time Leaderboard
      </h2>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-emerald-700">{stats.total_responses}</p>
            <p className="text-xs text-emerald-600">Responses Tracked</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-amber-700">{stats.quick_responses}</p>
            <p className="text-xs text-amber-600">Quick Responses</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{stats.quick_response_rate}%</p>
            <p className="text-xs text-blue-600">Quick Rate</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-purple-700">{stats.users_with_badge}</p>
            <p className="text-xs text-purple-600">Badge Holders</p>
          </div>
        </div>
      )}

      {/* Average Response Time */}
      {stats?.avg_response_time_minutes && (
        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-4 w-4 text-slate-500" />
            <span className="font-medium text-slate-900">Platform Average</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {stats.avg_response_time_minutes} minutes
          </p>
          <p className="text-xs text-slate-500">
            Average response time in last {stats.period_days} days
          </p>
        </div>
      )}

      {/* Settings */}
      <div className="space-y-5">
        {/* Enable Leaderboard */}
        <div className="flex items-center justify-between py-3 border-b border-slate-100">
          <div>
            <p className="font-medium text-slate-900">Enable Leaderboard</p>
            <p className="text-xs text-slate-500">Show fastest responders in search results</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`p-2 rounded-lg transition-colors ${
              settings.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
            }`}
            data-testid="leaderboard-enabled-toggle"
          >
            {settings.enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
          </button>
        </div>

        {/* Enable Badge */}
        <div className="flex items-center justify-between py-3 border-b border-slate-100">
          <div>
            <p className="font-medium text-slate-900 flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              Quick Responder Badge
            </p>
            <p className="text-xs text-slate-500">Award badges to fast responders</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, badge_enabled: !prev.badge_enabled }))}
            className={`p-2 rounded-lg transition-colors ${
              settings.badge_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
            }`}
            data-testid="badge-enabled-toggle"
          >
            {settings.badge_enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
          </button>
        </div>

        {/* Response Threshold */}
        <div className="py-3 border-b border-slate-100">
          <label className="block font-medium text-slate-900 mb-1 flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            Quick Response Threshold (minutes)
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Respond within this time to count as "quick"
          </p>
          <input
            type="number"
            value={settings.quick_responder_threshold_minutes}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              quick_responder_threshold_minutes: parseInt(e.target.value) || 5 
            }))}
            min={1}
            max={30}
            className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            data-testid="threshold-input"
          />
          <span className="ml-2 text-sm text-slate-500">minutes (default: 5)</span>
        </div>

        {/* Minimum Responses */}
        <div className="py-3 border-b border-slate-100">
          <label className="block font-medium text-slate-900 mb-1 flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            Minimum Responses for Badge
          </label>
          <p className="text-xs text-slate-500 mb-2">
            How many quick responses needed to earn badge
          </p>
          <input
            type="number"
            value={settings.minimum_responses_required}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              minimum_responses_required: parseInt(e.target.value) || 3 
            }))}
            min={1}
            max={20}
            className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            data-testid="min-responses-input"
          />
          <span className="ml-2 text-sm text-slate-500">responses (default: 3)</span>
        </div>

        {/* Leaderboard Size */}
        <div className="py-3 border-b border-slate-100">
          <label className="block font-medium text-slate-900 mb-1">
            Leaderboard Size
          </label>
          <p className="text-xs text-slate-500 mb-2">
            How many top responders to show
          </p>
          <select
            value={settings.leaderboard_size}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              leaderboard_size: parseInt(e.target.value) 
            }))}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            data-testid="leaderboard-size-select"
          >
            <option value={3}>Top 3</option>
            <option value={5}>Top 5 (recommended)</option>
            <option value={10}>Top 10</option>
          </select>
        </div>

        {/* Lookback Period */}
        <div className="py-3">
          <label className="block font-medium text-slate-900 mb-1">
            Stats Lookback Period
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Consider responses from the last X days
          </p>
          <select
            value={settings.leaderboard_lookback_days}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              leaderboard_lookback_days: parseInt(e.target.value) 
            }))}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            data-testid="lookback-select"
          >
            <option value={3}>3 days</option>
            <option value={7}>7 days (recommended)</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50"
        data-testid="save-leaderboard-settings-btn"
      >
        {saving ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="h-5 w-5" />
            Save Leaderboard Settings
          </>
        )}
      </button>
    </div>
  );
}
